import React from 'react';
import { MESSAGING_WINDOW_HOURS } from '@troia-v3/types';
import type { ConversationResponse } from '@troia-v3/types';

import { useTagStyle } from '@/shared/hooks/useTagCatalog';
import { Icon, IntegrationIcon } from '@/shared/icons';

import { useMessagingWindow } from '../../hooks';
import { formatRelativeDate } from '../../utils';
import { formatDateTime, formatResponseTime, formatShortDate } from './detailsFormat';
import type { DetailsData } from './useDetailsData';

interface DetailsHeaderProps {
  data: DetailsData;
  /** Gate de `contacts:update`: sem ele o botão de adicionar etiqueta some. */
  canUpdateContacts: boolean;
  onOpenTags: () => void;
  /**
   * O menu de mais ações, montado por quem usa o topo. É um nó, e não um
   * `onOpenActions`, porque o padrão do projeto (`CardActionsMenu`) traz o
   * próprio gatilho — um botão aqui só para chamar de volta daria dois
   * gatilhos, ou um botão que não abre nada.
   */
  actions?: React.ReactNode;
}

const TRACO = '—';

/** Fundo do avatar. É o mesmo cinza que o painel de hoje já usa — não é cor nova. */
const FUNDO_AVATAR = '#64748b';

/** A janela é da Meta, e o número dela tem dono em `@troia-v3/types`. */
const JANELA_MS = MESSAGING_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * Mapa fechado, e não `if`/`switch` com saída genérica: se um estado novo
 * entrar em `Conversation.status`, isto vira erro de compilação em vez de
 * escrever o valor cru em inglês na tela.
 */
const ROTULO_DO_STATUS: Record<ConversationResponse['status'], string> = {
  active: 'Ativa',
  waiting: 'Em espera',
  closed: 'Finalizada',
};

/**
 * Até duas iniciais. O `getInitials` do módulo devolve UMA letra (é o que a
 * lista de conversas mostra, num círculo bem menor); aqui o desenho pede duas
 * — "MA", "CA" —, então a conta é local em vez de mudar o helper compartilhado
 * e mexer, de tabela, em todas as telas que já dependem de uma letra só.
 *
 * Pedaço que não começa por letra ou número é descartado: sem isso o nome de
 * recurso "Conversa #…" viraria o avatar "C#".
 */
function initialsOf(name: string | undefined): string {
  const partes = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((parte) => /^[\p{L}\p{N}]/u.test(parte));
  const primeira = partes[0];
  if (!primeira) return TRACO;
  const ultima = partes.length > 1 ? partes[partes.length - 1] : undefined;
  const segundaLetra = ultima ? ultima.charAt(0) : '';
  return `${primeira.charAt(0)}${segundaLetra}`.toUpperCase();
}

/**
 * Quebra "03/06/2026" em "03/06" + "26": o ano entra no `<i>`, o degrau
 * tipográfico que o desenho reserva para a parte que não é o número em si
 * (o mesmo `<i>` do "/5" da satisfação).
 */
function splitYear(shortDate: string): { head: string; year: string | undefined } {
  const m = /^(\d{2}\/\d{2})\/\d{2}(\d{2})$/.exec(shortDate);
  const head = m?.[1];
  if (!head) return { head: shortDate, year: undefined };
  return { head, year: m?.[2] };
}

/**
 * Topo do painel de detalhes — "painel de números".
 *
 * Fica fixo acima da pilha de camadas: vale para o índice e para qualquer
 * sub-página aberta. Três faixas — identidade em UMA linha, etiquetas numa fila
 * que rola de lado, e a faixa de números.
 *
 * A célula da janela é a única com prazo e a única que pode não existir: a
 * janela de 24h só vem em canal Meta oficial e só em algumas rotas de leitura.
 * Quando falta, a faixa passa a três colunas em vez de deixar um buraco.
 */
const DetailsHeader: React.FC<DetailsHeaderProps> = ({
  data,
  canUpdateContacts,
  onOpenTags,
  actions,
}) => {
  const tagStyle = useTagStyle();
  const { conversation, isGroup, tags } = data;

  // Hook antes de qualquer saída: a ordem dos hooks não pode depender do dado.
  const janela = useMessagingWindow(conversation);

  if (!conversation) return null;

  const displayName =
    conversation.group?.name ||
    conversation.contact?.name ||
    conversation.subject ||
    `Conversa #${conversation.id.slice(0, 8)}`;

  const status = conversation.status;
  const provider = conversation.provider;
  const assignee = conversation.assignee;

  // Prazo da janela, lido UMA vez: decide se a célula existe e qual o tamanho
  // do fio de 2px rente à base dela.
  const fimDaJanela = ((): number | undefined => {
    const expiresAt = conversation.messagingWindow?.expiresAt;
    if (!expiresAt) return undefined;
    const ms = new Date(expiresAt).getTime();
    return Number.isNaN(ms) ? undefined : ms;
  })();
  const restamMs = fimDaJanela === undefined ? 0 : fimDaJanela - Date.now();

  // A célula só aparece com prazo VIVO. `useMessagingWindow` só devolve
  // `window_closed` em conversa ATIVA — em conversa finalizada, em espera ou com
  // contato bloqueado o rótulo continua chegando preenchido ("menos de 1
  // minuto", que é o piso do formatador) mesmo com a janela vencida, e mostrar
  // isso seria mostrar um número morto.
  const mostraJanela =
    janela.remainingLabel !== '' &&
    janela.state !== 'window_closed' &&
    conversation.messagingWindow?.isOpen !== false &&
    restamMs > 0;

  // Quanto do prazo de 24h ainda resta, para o fio de 2px rente à base.
  const restaDaJanela = Math.max(0, Math.min(100, Math.round((restamMs / JANELA_MS) * 100)));

  const csat = conversation.satisfaction;
  // 0 é "não coletada" (três tentativas inválidas), não nota zero.
  const temCsat = typeof csat === 'number' && csat > 0;

  const totalMensagens = conversation.messageCount;
  const primeiraResposta = formatResponseTime(conversation.firstResponseTime);

  const membros = conversation.members?.length;
  const criadoEm = splitYear(formatShortDate(conversation.createdAt));

  return (
    <header className="cdp-top" data-testid="details-header">
      {/* 1 · identidade: quem é, em que estado, e de quem é o atendimento */}
      <div className="cdp-id">
        <span className="cdp-av" style={{ backgroundColor: FUNDO_AVATAR }} aria-hidden="true">
          {isGroup ? <Icon name="users" size={18} /> : initialsOf(displayName)}
        </span>

        <span className="cdp-idtx">
          <span className="cdp-name" title={displayName}>
            {displayName}
          </span>
          <span className="cdp-sub">
            <i className={status === 'active' ? 'cdp-dot' : 'cdp-dot cdp-dot--idle'} />
            {ROTULO_DO_STATUS[status]}
            {provider ? (
              <>
                {' ·'}
                <IntegrationIcon id={provider.type} size={12} variant="mono" className="cdp-chan" />
                {/* Só o nome do canal encolhe: ele vem do cliente e pode ser longo
                    ("HENRIQUE Integration"), enquanto o estado é curto e precisa
                    continuar legível. Sem isto a linha quebrava em duas e o topo
                    inteiro passava de 139 para 170px. */}
                <span className="cdp-subchan" title={provider.name}>
                  {provider.name}
                </span>
              </>
            ) : null}
          </span>
        </span>

        {assignee ? (
          <span
            className="cdp-owner"
            title={`Atendimento de ${assignee.name}`}
            data-testid="details-header-owner"
          >
            {initialsOf(assignee.name)}
          </span>
        ) : null}

        {actions}
      </div>

      {/* 2 · etiquetas: fila ÚNICA que rola de lado, com o "+" preso à direita e
          FORA da rolagem — é o que impede o botão de ficar órfão numa segunda fila. */}
      {!isGroup && (
        <div className="cdp-tagrow" data-testid="details-tags">
          <div className="cdp-tags">
            {tags.length === 0 ? (
              // Fila vazia continua existindo: some a etiqueta, não o lugar dela.
              <span className="cdp-tag" style={{ color: 'var(--theme-muted-text)' }}>
                Sem etiquetas
              </span>
            ) : (
              // Desde 22/09/2026 cada etiqueta tem a COR que alguém escolheu
              // para ela no catálogo (Configurações → Tags). Antes todas eram
              // iguais, e era a decisão certa enquanto tag era texto livre:
              // destacar por nome só acertaria quem escrevesse exatamente a
              // palavra cravada no código. Agora a cor é um dado, não um
              // palpite. Etiqueta fora do catálogo cai no mesmo hash que o
              // catálogo usa — nenhuma fica cinza.
              tags.map((tag) => (
                <span
                  key={tag}
                  className="cdp-tag"
                  style={tagStyle(tag)}
                  data-testid={`details-tag-${tag}`}
                >
                  {tag}
                </span>
              ))
            )}
          </div>
          {canUpdateContacts && (
            <button
              data-testid="details-add-tag"
              type="button"
              className="cdp-addtag"
              title="Adicionar etiqueta"
              aria-label="Adicionar etiqueta"
              onClick={onOpenTags}
            >
              <Icon name="plus" size={12} />
            </button>
          )}
        </div>
      )}

      {/* 3 · a faixa de números */}
      {isGroup ? (
        <div className="cdp-nums cdp-nums--3">
          <span
            className="cdp-cell"
            data-testid="details-metric-people"
            title={
              membros === undefined
                ? 'Número de pessoas no grupo indisponível'
                : `${membros} ${membros === 1 ? 'pessoa' : 'pessoas'} no grupo`
            }
          >
            <span className="cdp-n tabular-nums">{membros ?? TRACO}</span>
            <span className="cdp-l">pessoas</span>
          </span>

          <span
            className="cdp-cell"
            data-testid="details-metric-last"
            title={
              conversation.lastMessageAt
                ? `Última mensagem em ${formatDateTime(conversation.lastMessageAt)}`
                : 'Nenhuma mensagem nesta conversa'
            }
          >
            <span className="cdp-n tabular-nums">
              {/* `formatRelativeDate` já é o dono desse texto no módulo (é o que a
                  lista de conversas mostra): "10:14", "ontem", "sex", "12/08/26". */}
              {conversation.lastMessageAt ? formatRelativeDate(conversation.lastMessageAt) : TRACO}
            </span>
            <span className="cdp-l">última mensagem</span>
          </span>

          <span
            className="cdp-cell"
            data-testid="details-metric-created"
            title={`Grupo criado em ${formatDateTime(conversation.createdAt)}`}
          >
            <span className="cdp-n tabular-nums">
              {criadoEm.head}
              {criadoEm.year ? <i>/{criadoEm.year}</i> : null}
            </span>
            <span className="cdp-l">criado em</span>
          </span>
        </div>
      ) : (
        <div className={mostraJanela ? 'cdp-nums' : 'cdp-nums cdp-nums--3'}>
          <span
            className="cdp-cell"
            data-testid="details-metric-messages"
            title={`${totalMensagens} ${totalMensagens === 1 ? 'mensagem' : 'mensagens'} · conversa iniciada em ${formatDateTime(conversation.startedAt)}`}
          >
            <span className="cdp-n tabular-nums">{totalMensagens}</span>
            <span className="cdp-l">mensagens</span>
          </span>

          <span
            className="cdp-cell"
            data-testid="details-metric-response"
            title={
              conversation.firstResponseTime === undefined
                ? 'Primeira resposta ainda não registrada'
                : `Primeira resposta em ${primeiraResposta}`
            }
          >
            <span className="cdp-n tabular-nums">{primeiraResposta}</span>
            <span className="cdp-l">1ª resposta</span>
          </span>

          <span
            className="cdp-cell"
            data-testid="details-metric-csat"
            title={
              temCsat
                ? `Satisfação da conversa: ${csat} de 5`
                : 'Satisfação não coletada nesta conversa'
            }
          >
            <span className="cdp-n tabular-nums">
              {temCsat ? (
                <>
                  {csat}
                  <i>/5</i>
                </>
              ) : (
                TRACO
              )}
            </span>
            <span className="cdp-l">satisfação</span>
          </span>

          {mostraJanela && (
            <span
              className="cdp-cell cdp-cell--win"
              data-testid="details-metric-window"
              title={`A janela de 24 horas para enviar mensagem fecha em ${janela.remainingLabel}`}
            >
              <span className="cdp-n tabular-nums">{janela.remainingLabel}</span>
              <span className="cdp-l">
                <Icon name="message-circle" />
                janela
              </span>
              <span className="cdp-bar" aria-hidden="true">
                <i style={{ width: `${restaDaJanela}%` }} />
              </span>
            </span>
          )}
        </div>
      )}
    </header>
  );
};

export default DetailsHeader;
